import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { ArrowLeft, Lock, Flame, Loader2, ShoppingCart, MessageCircle, AlertTriangle, Check, Sparkles, Unlock } from 'lucide-react';
import { LanguageSelector } from '../components/LanguageSelector';
import { MyCompanionLogo } from '../components/MyCompanionLogo';
import { toast } from 'sonner';
import { usePurchase } from '../hooks/usePurchase';
import { usePlayPrices } from '../hooks/usePlayPrices';
import { runPurchaseWithRetry } from '../utils/purchaseRetry';
import PurchaseCelebrationModal from '../components/PurchaseCelebrationModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const levelConfig = [
  { level: 1, color: 'from-terracotta-light to-terracotta-main', flames: 1 },
  { level: 2, color: 'from-terracotta-main to-terracotta-dark', flames: 2 },
  { level: 3, color: 'from-sage-main to-terracotta-light', flames: 3 },
  { level: 4, color: 'from-sage-main to-sage-dark', flames: 4 },
  { level: 5, color: 'from-[#C99A43] to-terracotta-dark', flames: 5 }
];

export default function SelectLevelPage() {
  const navigate = useNavigate();
  const { gender, characterId } = useParams();
  const { user, token, logout, getCharacterDialogues, isGuest } = useAuth();
  const { t, language } = useLanguage();
  const { startPurchase: _basePurchase } = usePurchase();
  const startPurchase = React.useCallback(
    (params) => runPurchaseWithRetry({ startPurchase: _basePurchase, params, toast, language }),
    [_basePurchase, language]
  );
  const playPrices = usePlayPrices();
  const PRICE_PROMO = playPrices.oneTime.promoNum ?? 1.49;
  const PRICE_ORIGINAL = playPrices.oneTime.fullNum ?? PRICE_PROMO;
  const HAS_PROMO = PRICE_ORIGINAL > PRICE_PROMO;
  const fmt = playPrices.formatCurrency;
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [purchasingLevel, setPurchasingLevel] = useState(null);
  const [guestWarningOpen, setGuestWarningOpen] = useState(false);
  const [pendingPurchaseLevels, setPendingPurchaseLevels] = useState([]);
  const [celebration, setCelebration] = useState(null);

  const currentDialogues = getCharacterDialogues(characterId);

  useEffect(() => {
    // Ensure the page starts at the top (some browsers / TWA WebView preserve
    // scroll position from the previous route, which made the entry animation
    // appear to start from the bottom of the viewport).
    window.scrollTo(0, 0);
    fetchLevels();
  }, [token, characterId]);

  const fetchLevels = async () => {
    try {
      const response = await axios.get(`${API}/levels/${characterId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLevels(response.data);
    } catch (error) {
      console.error('Error fetching levels:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = (levelData) => {
    if (!levelData.is_unlocked) return;
    navigate(`/chat/${gender}/${characterId}/${levelData.level}`);
  };

  const lockedLevels = levels.filter(l => l.can_purchase);
  const lockedCount = lockedLevels.length;
  const totalPriceAll = lockedCount * PRICE_PROMO;
  const totalOriginalAll = lockedCount * PRICE_ORIGINAL;

  const executePurchase = async (levelsArray) => {
    const lvls = Array.isArray(levelsArray) ? levelsArray : [levelsArray];
    setPurchasing(true);
    setPurchasingLevel(lvls.length === 1 ? lvls[0] : 'all');
    try {
      const maxLevel = Math.max(...lvls);
      const returnPath = `/chat/${gender}/${characterId}/${maxLevel}`;
      const res = await startPurchase({
        itemType: 'level',
        itemId: `${characterId}:${lvls.sort((a, b) => a - b).join(',')}`,
        returnTo: returnPath,
      });
      if (res.redirected) return;
      if (res.success) {
        // Re-fetch level data so UI reflects new unlocks
        fetchLevels();
        setCelebration({ unlockedCount: lvls.length, maxLevel: Math.max(...lvls) });
      } else if (!res.cancelled) {
        toast.error(res.message || (language === 'fr' ? "Erreur lors de l'achat" : 'Purchase error'));
      }
    } catch (error) {
      console.error('Purchase error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'achat');
    } finally {
      setPurchasing(false);
      setPurchasingLevel(null);
    }
  };

  // Calculate cumulative price and levels needed to reach each level
  const getCumulativeInfo = (targetLevel) => {
    const levelsToUnlock = levels.filter(l => !l.is_unlocked && l.level > 1 && l.level <= targetLevel);
    const count = levelsToUnlock.length;
    const price = count * PRICE_PROMO;
    const originalPrice = count * PRICE_ORIGINAL;
    const levelNums = levelsToUnlock.map(l => l.level);
    return { count, price, originalPrice, levelNums };
  };

  const handlePurchaseUpTo = (targetLevel) => {
    const { levelNums } = getCumulativeInfo(targetLevel);
    if (levelNums.length === 0) return;
    if (isGuest) {
      setPendingPurchaseLevels(levelNums);
      setGuestWarningOpen(true);
      return;
    }
    executePurchase(levelNums);
  };

  const handleBuyAll = () => {
    const allLockedNums = lockedLevels.map(l => l.level);
    if (allLockedNums.length === 0) return;
    if (isGuest) {
      setPendingPurchaseLevels(allLockedNums);
      setGuestWarningOpen(true);
      return;
    }
    executePurchase(allLockedNums);
  };

  const confirmGuestPurchase = () => {
    setGuestWarningOpen(false);
    if (pendingPurchaseLevels.length > 0) {
      executePurchase(pendingPurchaseLevels);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-velvet-bg flex items-center justify-center">
        <Loader2 className="animate-spin text-purple-main" size={40} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-velvet-bg">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate(`/select-character/${gender}`)}
            className="flex items-center gap-2 text-velvet_text-secondary hover:text-velvet_text-accent transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <MyCompanionLogo size="sm" />
          </button>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={logout}
                className="text-velvet_text-secondary hover:text-velvet_text-primary text-sm"
                data-testid="logout-button"
              >
                {t('logout')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-40 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-3">
            <span className="text-velvet_text-accent text-sm font-medium tracking-wider uppercase">
              {({fr: 'Étape 3 sur 3', en: 'Step 3 of 3', es: 'Paso 3 de 3', de: 'Schritt 3 von 3', pt: 'Passo 3 de 3'})[language] || 'Step 3 of 3'}
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-center gradient-text mb-3 animate-slideDown">
            {t('choose_level')}
          </h1>

          {/* Dialogues counter */}
          <div className="glass rounded-xl p-3 mb-6 animate-slideDown">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageCircle size={14} className="text-purple-light" />
                <span className="text-velvet_text-secondary text-xs">
                  {language === 'fr' ? `Dialogues avec ${characterId}` : language === 'es' ? `Diálogos con ${characterId}` : language === 'de' ? `Dialoge mit ${characterId}` : language === 'pt' ? `Diálogos com ${characterId}` : `Dialogues with ${characterId}`}
                </span>
              </div>
              <span className="text-velvet_text-accent text-xs font-bold">{currentDialogues}</span>
            </div>
          </div>

          {/* Level cards */}
          <div className="space-y-3">
            {levels.map((levelData, index) => {
              const config = levelConfig[levelData.level - 1];
              const isUnlocked = levelData.is_unlocked;
              const canPurchase = levelData.can_purchase;
              const canUnlockFree = levelData.can_unlock_by_dialogues;
              const dialoguesRequired = levelData.dialogues_required;
              const dialoguesCurrent = levelData.dialogues_current || 0;
              const progressPercent = dialoguesRequired > 0
                ? Math.min(100, (dialoguesCurrent / dialoguesRequired) * 100)
                : 100;
              const isPurchasing = purchasingLevel === levelData.level;
              const cumul = getCumulativeInfo(levelData.level);

              return (
                <div
                  key={levelData.level}
                  onClick={() => isUnlocked && handleSelect(levelData)}
                  className={`group relative overflow-hidden rounded-xl glass p-4 sm:p-5 animate-slideDown transition-all duration-200
                    ${isUnlocked ? 'cursor-pointer hover:ring-1 hover:ring-purple-light/40' : ''}
                  `}
                  style={{ animationDelay: `${index * 80}ms` }}
                  data-testid={`level-card-${levelData.level}`}
                >
                  <div className="flex items-start gap-3">
                    {/* Status icon */}
                    <div className="w-8 flex items-center justify-center pt-1 shrink-0">
                      {isUnlocked ? (
                        <div className="w-7 h-7 rounded-full bg-green-500/20 flex items-center justify-center">
                          <Check size={16} className="text-green-400" />
                        </div>
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center">
                          <Lock size={14} className="text-gray-500" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-heading text-lg text-velvet_text-primary">
                          {t(`level_${levelData.level}`)}
                        </h3>
                        {/* Flames - à droite du nom */}
                        <div className="flex gap-0.5">
                          {[...Array(config.flames)].map((_, i) => (
                            <Flame
                              key={i}
                              size={14}
                              className={`${isUnlocked ? 'text-purple-light' : 'text-gray-600'} fill-current`}
                            />
                          ))}
                        </div>
                        {levelData.is_free && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
                            {t('free')}
                          </span>
                        )}
                        {isUnlocked && !levelData.is_free && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-main/20 text-purple-light">
                            {({fr: 'Débloqué', en: 'Unlocked', es: 'Desbloqueado', de: 'Freigeschaltet', pt: 'Desbloqueado'})[language] || 'Unlocked'}
                          </span>
                        )}
                      </div>

                      <p className="text-velvet_text-secondary text-xs mb-2">
                        {t(`level_${levelData.level}_desc`)}
                      </p>

                      {/* Progress bar for any locked level — shows X/Y dialogues toward unlock */}
                      {!isUnlocked && dialoguesRequired > 0 && (
                        <div className="mb-2">
                          <div className="flex justify-between text-xs mb-1">
                            <span className="text-velvet_text-secondary">
                              {({fr: 'Progression gratuite', en: 'Free progress', es: 'Progresión gratuita', de: 'Kostenloser Fortschritt', pt: 'Progressão gratuita'})[language] || 'Free progress'}
                            </span>
                            <span className="text-velvet_text-accent">
                              {dialoguesCurrent}/{dialoguesRequired}
                            </span>
                          </div>
                          <Progress value={progressPercent} className="h-1.5" />
                          {canUnlockFree && (
                            <p className="text-xs text-green-400 mt-1">
                              {({fr: 'Objectif atteint ! Ce niveau se débloquera automatiquement.', en: 'Goal reached! This level will unlock automatically.', es: '¡Objetivo alcanzado! Este nivel se desbloqueará automáticamente.', de: 'Ziel erreicht! Diese Stufe wird automatisch freigeschaltet.', pt: 'Objetivo atingido! Este nível será desbloqueado automaticamente.'})[language] || 'Goal reached! This level will unlock automatically.'}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className="flex items-center gap-2 flex-wrap">
                        {isUnlocked ? (
                          <Button
                            onClick={(e) => { e.stopPropagation(); handleSelect(levelData); }}
                            className={`bg-gradient-to-r ${config.color} text-white rounded-lg px-4 py-2 text-sm h-auto`}
                            data-testid={`select-level-${levelData.level}`}
                          >
                            {t('select')}
                          </Button>
                        ) : canPurchase ? (
                          <Button
                            onClick={(e) => { e.stopPropagation(); handlePurchaseUpTo(levelData.level); }}
                            disabled={purchasing}
                            className="bg-gradient-to-r from-purple-main to-fuchsia-main hover:opacity-90 text-white rounded-lg px-4 py-2 text-sm h-auto"
                            data-testid={`buy-level-${levelData.level}`}
                          >
                            {isPurchasing ? (
                              <Loader2 className="animate-spin mr-1" size={14} />
                            ) : (
                              <Unlock size={14} className="mr-1.5" />
                            )}
                            {HAS_PROMO && cumul.count > 1 && (
                              <span className="line-through opacity-60 mr-1 text-xs">{fmt(cumul.originalPrice)}</span>
                            )}
                            {HAS_PROMO && cumul.count === 1 && (
                              <span className="line-through opacity-60 mr-1 text-xs">{fmt(PRICE_ORIGINAL)}</span>
                            )}
                            <span className="font-bold">{fmt(cumul.price)}</span>
                            {cumul.count > 1 && (
                              <span className="ml-1 text-xs opacity-80">({cumul.count} niv.)</span>
                            )}
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {/* Bottom gradient bar */}
                  <div className="absolute bottom-0 left-0 right-0 h-1">
                    <div
                      className={`h-full bg-gradient-to-r ${config.color} ${isUnlocked ? 'opacity-100' : 'opacity-20'}`}
                      style={{ width: `${levelData.level * 20}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Info box */}
          <div className="mt-6 glass rounded-xl p-4 animate-slideDown">
            <ul className="text-velvet_text-secondary text-xs space-y-1.5">
              <li className="flex items-start gap-2">
                <MessageCircle size={14} className="text-purple-light mt-0.5 shrink-0" />
                <span>
                  {language === 'fr'
                    ? 'Les niveaux 1 à 3 se débloquent aussi gratuitement en discutant'
                    : 'Levels 1 to 3 also unlock for free by chatting'
                  }
                </span>
              </li>
              <li className="flex items-start gap-2">
                <Sparkles size={14} className="text-purple-light mt-0.5 shrink-0" />
                <span>
                  {language === 'fr'
                    ? 'Prix de lancement : chaque niveau à 1,49 € au lieu de 2,99 €'
                    : 'Launch price: each level at €1.49 instead of €2.99'
                  }
                </span>
              </li>
            </ul>
          </div>
        </div>
      </main>

      {/* Sticky bottom bar: Buy all remaining levels */}
      {lockedCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 glass border-t border-purple-main/30" data-testid="buy-all-bar">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3">
            <Button
              onClick={handleBuyAll}
              disabled={purchasing}
              className="w-full bg-gradient-to-r from-purple-main to-fuchsia-main hover:opacity-90 text-white rounded-xl py-4 text-base font-medium h-auto flex flex-col items-center gap-1"
              data-testid="buy-all-button"
            >
              {purchasing && purchasingLevel === 'all' ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                <>
                  <span className="flex items-center gap-2">
                    <ShoppingCart size={18} />
                    {language === 'fr'
                      ? `Tout débloquer (${lockedCount} niveau${lockedCount > 1 ? 'x' : ''})`
                      : `Unlock all (${lockedCount} level${lockedCount > 1 ? 's' : ''})`
                    }
                  </span>
                  <span className="text-sm opacity-90">
                    {HAS_PROMO && (
                      <span className="line-through opacity-60 mr-1">{fmt(totalOriginalAll)}</span>
                    )}
                    <span className="font-bold">{fmt(totalPriceAll)}</span>
                    {lockedCount > 1 && (
                      <span className="ml-1 opacity-70">
                        ({({fr: 'soit', en: 'i.e.', es: 'es decir', de: 'd.h.', pt: 'ou seja'})[language] || 'i.e.'} {fmt(PRICE_PROMO)} / {({fr: 'niveau', en: 'level', es: 'nivel', de: 'Stufe', pt: 'nível'})[language] || 'level'})
                      </span>
                    )}
                  </span>
                </>
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Guest Purchase Warning Modal */}
      <AlertDialog open={guestWarningOpen} onOpenChange={setGuestWarningOpen}>
        <AlertDialogContent className="bg-velvet-bg border border-purple-main/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-velvet_text-primary flex items-center gap-2">
              <AlertTriangle className="text-amber-400" size={20} />
              {({fr: 'Attention - Mode Invité', en: 'Warning - Guest Mode', es: 'Atención - Modo Invitado', de: 'Achtung - Gastmodus', pt: 'Atenção - Modo Convidado'})[language] || 'Warning - Guest Mode'}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-velvet_text-secondary">
              {language === 'fr'
                ? 'Vous êtes en mode invité. Vos achats ne seront PAS sauvegardés si vous quittez l\'application sans créer de compte. Créez un compte après votre achat pour conserver vos achats.'
                : 'You are in guest mode. Your purchases will NOT be saved if you leave the app without creating an account. Create an account after your purchase to keep your purchases.'
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              className="border-purple-main/50 text-velvet_text-secondary hover:bg-purple-main/10"
              data-testid="guest-warning-cancel"
            >
              {({fr: 'Annuler', en: 'Cancel', es: 'Cancelar', de: 'Abbrechen', pt: 'Cancelar'})[language] || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmGuestPurchase}
              className="bg-gradient-to-r from-purple-main to-fuchsia-main text-white hover:opacity-90"
              data-testid="guest-warning-confirm"
            >
              {({fr: 'Continuer l\'achat', en: 'Continue purchase', es: 'Continuar compra', de: 'Kauf fortsetzen', pt: 'Continuar compra'})[language] || 'Continue purchase'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PurchaseCelebrationModal
        open={!!celebration}
        title={
          ({
            fr: celebration?.unlockedCount > 1 ? 'Niveaux débloqués !' : 'Niveau débloqué !',
            en: celebration?.unlockedCount > 1 ? 'Levels unlocked!' : 'Level unlocked!',
            es: celebration?.unlockedCount > 1 ? '¡Niveles desbloqueados!' : '¡Nivel desbloqueado!',
            de: celebration?.unlockedCount > 1 ? 'Stufen freigeschaltet!' : 'Stufe freigeschaltet!',
            pt: celebration?.unlockedCount > 1 ? 'Níveis desbloqueados!' : 'Nível desbloqueado!',
          })[language] || 'Unlocked!'
        }
        message={
          ({
            fr: 'L\'intensité de votre conversation peut maintenant monter d\'un cran. À toi de jouer',
            en: 'Your conversation can now reach a deeper level of intensity. Have fun',
            es: 'La intensidad de tu conversación puede subir de nivel. Diviértete',
            de: 'Die Intensität deines Gesprächs kann jetzt eine Stufe höher gehen. Viel Spaß',
            pt: 'A intensidade da tua conversa pode agora subir um nível. Diverte-te',
          })[language] || 'Enjoy the new level'
        }
        continueLabel={
          ({
            fr: 'Continuer la conversation',
            en: 'Continue the conversation',
            es: 'Continuar la conversación',
            de: 'Unterhaltung fortsetzen',
            pt: 'Continuar a conversa',
          })[language] || 'Continue'
        }
        onContinue={() => {
          const maxLvl = celebration?.maxLevel;
          setCelebration(null);
          if (maxLvl) navigate(`/chat/${gender}/${characterId}/${maxLvl}`);
        }}
      />
    </div>
  );
}
